import React, { useState, useEffect, useMemo } from 'react';
import { SkillConfig } from '../../types/onboarding';
import { Image } from '../../../ui/Image';
import { useCreateSkill } from '../../apis/skill-registry';
import { Button } from '../../components';
import { useGetOutboundIntegrations } from '../../apis/outbound-integrations';

import {
  ToggleGroupRoot,
  ToggleGroupItem,
  Select,
  Input,
} from '@plerionhq/design-system';

import { useTranslation } from 'react-i18next';

interface DrataSkillSetupProps {
  skill: SkillConfig;
  onComplete: () => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export const DrataSkillSetup: React.FC<DrataSkillSetupProps> = ({
  skill,
  onComplete,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useTranslation('pleri/onboarding');
  const [selectedIntegrationId, setSelectedIntegrationId] = useState(
    skill.config?.integrationId ?? '',
  );
  const [apiKey, setApiKey] = useState<string>(
    typeof skill.config?.apiKey === 'string' ? skill.config.apiKey : '',
  );
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const TAB_TYPES = {
    EXISTING: 'existing',
    NEW: 'new',
  } as const;

  type TabType = (typeof TAB_TYPES)[keyof typeof TAB_TYPES];
  const [activeTab, setActiveTab] = useState<TabType>(TAB_TYPES.EXISTING);
  const { mutateAsync: enableSkill, isPending: isEnabling } = useCreateSkill();
  const { data: integrations, isLoading: isLoadingIntegrations } =
    useGetOutboundIntegrations();

  const drataOptions = useMemo(
    () =>
      integrations
        ?.filter((integration) => integration.type === 'Drata')
        .map((integration) => ({
          name: integration.name,
          value: integration.integrationId,
        })) ?? [],
    [integrations],
  );

  // Auto-select if there's only one integration
  useEffect(() => {
    if (
      drataOptions.length === 1 &&
      !selectedIntegrationId &&
      activeTab === TAB_TYPES.EXISTING
    ) {
      setSelectedIntegrationId(drataOptions[0].value);
    }
  }, [drataOptions, activeTab, selectedIntegrationId, TAB_TYPES.EXISTING]);

  const handleSubmit = async () => {
    if (isDisabled) {
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      if (activeTab === TAB_TYPES.EXISTING) {
        await enableSkill({
          skillType: skill.id,
          enabled: true,
          config: {
            integrationId: selectedIntegrationId,
          },
        });
      } else {
        await enableSkill({
          skillType: skill.id,
          enabled: true,
          secrets: {
            apiKey,
          },
        });
      }
      await onComplete();
    } catch (error) {
      console.error('Failed to setup Drata skill:', error);
      setError(t('failedToConfigureDrataPleaseTryAgain'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isReconfiguring = skill.enabled;
  const hasSingleIntegration = drataOptions.length === 1;
  const hasMultipleIntegrations = drataOptions.length > 1;

  const isDisabled =
    isLoading ||
    isEnabling ||
    isSubmitting ||
    (activeTab === TAB_TYPES.EXISTING &&
      (isLoadingIntegrations || !selectedIntegrationId)) ||
    (activeTab === TAB_TYPES.NEW && !apiKey?.trim());

  const selectedOption = drataOptions.find(
    (option) => option.value === selectedIntegrationId,
  );

  return (
    <div className="mx-auto max-w-2xl">
      {/* 1. Title */}
      <div className="mb-8 text-center">
        <h2 className="text-2xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
          {t('drata')}
        </h2>
      </div>
      <div className="rounded-lg bg-white p-6 dark:bg-gray-900">
        {/* 2. Logo */}
        <div className="mb-8 text-center">
          <div className="relative mx-auto inline-block">
            <div className="absolute inset-0 animate-[pulse_4s_ease-in-out_infinite] rounded-xl bg-green-50 blur-xl dark:bg-green-950/10" />
            {skill.image ? (
              <Image
                src={skill.image.src}
                alt={skill.image.alt}
                className="relative h-20 rounded-xl object-cover"
              />
            ) : (
              <div className="relative flex h-20 w-20 items-center justify-center rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-600/20">
                <span className="text-4xl leading-tight">{skill.icon}</span>
              </div>
            )}
          </div>
        </div>

        {/* 3. Short Description */}
        <div className="mb-8 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            {t('iInDrataMakingSureComplianceIsn')}
          </p>
        </div>

        {/* 4. Learn More Link */}
        <div className="mb-8 text-center">
          <p className="text-sm text-gray-500">
            <a
              href="https://docs.pleri.ai/tools/drata"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-700"
            >
              {t('clickHereToLearnMore')}
            </a>
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-8 rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-950">
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* 5. Form Field */}
        <div className="mb-8">
          <div className="mx-auto flex max-w-md justify-center">
            <ToggleGroupRoot
              type="single"
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as TabType)}
            >
              <ToggleGroupItem
                value={TAB_TYPES.EXISTING}
                aria-label={t('useExistingIntegration')}
                position="first"
              >
                {t('useExistingIntegration')}
              </ToggleGroupItem>

              <ToggleGroupItem
                value={TAB_TYPES.NEW}
                aria-label={t('configureNewIntegration')}
                position="last"
              >
                {t('configureNewIntegration')}
              </ToggleGroupItem>
            </ToggleGroupRoot>
          </div>
        </div>

        {/* Integration Configuration */}
        <div className="mb-8">
          <div className="mx-auto max-w-md">
            {activeTab === TAB_TYPES.EXISTING ? (
              // Existing Integration Tab
              <>
                {isLoadingIntegrations ? (
                  // Show loading state while fetching integrations
                  <div className="w-full">
                    <div className="flex w-full items-center gap-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600 dark:border-gray-600" />
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('loadingYourDrata')}
                      </p>
                    </div>
                  </div>
                ) : hasSingleIntegration ? (
                  // Show selected integration when there's only one
                  <div className="w-full">
                    <div className="flex w-full items-center gap-2 rounded-lg bg-blue-50 p-2 dark:bg-blue-950">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                        <svg
                          className="h-3 w-3 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <p className="text-sm text-blue-800">
                        {selectedOption?.name || t('drataIntegration')}
                      </p>
                    </div>
                  </div>
                ) : hasMultipleIntegrations ? (
                  // Show dropdown when there are multiple integrations
                  <>
                    <Select
                      options={drataOptions}
                      defaultSelectedIndex={drataOptions.findIndex(
                        (option) => option.value === selectedIntegrationId,
                      )}
                      placeholder={t('chooseYourDrata')}
                      onUpdate={(_, selected) =>
                        setSelectedIntegrationId(selected?.value as string)
                      }
                      wrapperClassName="w-full !gap-0 text-sm"
                      hideCloseIcon={true}
                    />
                  </>
                ) : null}
              </>
            ) : (
              // New Integration Tab
              <div className="space-y-4">
                <div className="w-full">
                  <Input
                    id="api-key"
                    type="password"
                    value={apiKey || ''}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={t('enterYourDrataApiKey')}
                    className="h-[39px] w-full"
                    disabled={isLoading || isEnabling || isSubmitting}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 6. Call to Action Button */}
        {!isLoadingIntegrations && (
          <div className="mb-6 flex flex-col items-center">
            <Button
              onClick={handleSubmit}
              disabled={isDisabled}
              className={`group relative flex w-full items-center justify-center rounded-lg px-8 py-3 text-sm font-medium transition-all ${
                isDisabled
                  ? 'cursor-not-allowed bg-gray-300 text-gray-500 dark:bg-gray-600 dark:text-gray-400'
                  : ''
              }`}
            >
              <span>
                {isReconfiguring
                  ? t('updateConfiguration')
                  : t('activateDrata')}
              </span>
            </Button>
          </div>
        )}

        {/* 7. Cancel or Go Back Button */}
        {!isLoadingIntegrations && (
          <div className="flex flex-col items-center">
            <button
              onClick={onCancel}
              disabled={isDisabled}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              {t('common:cancel')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
